import React from 'react';
import { DistrictStandingRow } from '../../sim/districtEngine';
import { DataList } from './DataList';
import { useGameStore } from '../../store/gameStore';
import { rulesForState } from '../../sim/stateRules';

const diff = (n: number) => (n > 0 ? `+${n}` : `${n}`);
const diffColor = (n: number) => (n >= 0 ? '#047857' : '#B91C1C');

/**
 * District standings, mobile-first: one compact row per team on phones (seed, school, then "W-L (W-L) +XX":
 * overall record, district record and capped point differential); a full table on desktop. Playoff spots are shaded.
 */
export const StandingsList: React.FC<{ rows: DistrictStandingRow[]; highlightTeamId?: string; dense?: boolean }> = ({ rows, highlightTeamId, dense }) => {
  const openTeamProfile = useGameStore((s) => s.openTeamProfile);
  const districtLabel = rulesForState(useGameStore((s) => s.league?.state)).districtLabel;
  const teamLink = (r: DistrictStandingRow) => (
    <button onClick={() => openTeamProfile(r.teamId)} style={nameBtn}>
      {r.name}
    </button>
  );
  return (
    <>
      <div className="ui-standings">
        {rows.map((row) => (
          <div
            key={row.teamId}
            className="ui-standings-row"
            style={{ background: row.isPlayoffBound ? '#F0FDF4' : undefined, fontWeight: row.teamId === highlightTeamId ? 'bold' : undefined, minHeight: dense ? '40px' : undefined }}
          >
            <span className="ui-standings-seed" style={{ background: row.isPlayoffBound ? '#15803D' : '#E2E8F0', color: row.isPlayoffBound ? '#fff' : '#334155' }}>
              {row.rank}
            </span>
            <span className="ui-standings-name">{teamLink(row)}</span>
            {/* One line, the standard way: overall record, district record in parentheses, point differential */}
            <span className="ui-standings-line">
              <strong>{row.overallRecord}</strong>
              <small>({row.districtRecord})</small>
              <span className="ui-standings-diff" style={{ color: diffColor(row.pointDifferential) }}>
                {diff(row.pointDifferential)}
              </span>
            </span>
          </div>
        ))}
      </div>
      <div className="ui-desktop-only">
        <DataList
          rows={rows}
          rowKey={(r) => r.teamId}
          rowTone={(r) => (r.isPlayoffBound ? '#F0FDF4' : undefined)}
          columns={[
            { key: 'seed', label: 'Seed', render: (r) => <strong>#{r.rank}</strong> },
            { key: 'school', label: 'School', primary: true, render: (r) => <strong>{teamLink(r)}</strong> },
            { key: 'district', label: districtLabel, render: (r) => r.districtRecord },
            { key: 'overall', label: 'Overall', render: (r) => r.overallRecord },
            { key: 'diff', label: 'Diff (capped)', render: (r) => <span style={{ color: diffColor(r.pointDifferential) }}>{diff(r.pointDifferential)}</span> },
            {
              key: 'status',
              label: 'Status',
              render: (r) => (
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: r.isPlayoffBound ? '#15803D' : '#64748B' }}>{r.isPlayoffBound ? '🏆 Playoff spot' : 'Out'}</span>
              )
            }
          ]}
        />
      </div>
    </>
  );
};

const nameBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'inherit',
  font: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  // A full-height tap target on phones (the row is taller than the text)
  minHeight: '40px',
  display: 'inline-flex',
  alignItems: 'center'
};
